import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const C = {
  pine:    '#1a3d2e',
  forest:  '#2d5a3d',
  sage:    '#4a7c59',
  fern:    '#6aab7a',
  mint:    '#a8d5b0',
  foam:    '#e8f5eb',
  teal:    '#0d9488',
  tealDk:  '#0f766e',
  cream:   '#faf9f6',
  white:   '#ffffff',
  ink:     '#111815',
  inkMid:  '#3d4f45',
  inkLt:   '#7a8c81',
  border:  '#dde8e0',
  purple:  '#7c3aed',
  purpleDk:'#6d28d9',
}

const DISPLAY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital,wght@0,400;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes blob1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(40px,-30px) scale(1.07)} 66%{transform:translate(-20px,18px) scale(0.94)} }
  @keyframes blob2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-50px,24px) scale(1.06)} 66%{transform:translate(28px,-40px) scale(0.93)} }
  .login-card { animation: fadeUp .6s cubic-bezier(.22,1,.36,1) both; }
  .login-input {
    width: 100%; padding: 13px 16px; font-size: 15px;
    border: 1.5px solid ${C.border}; border-radius: 12px;
    color: ${C.ink}; background: white; font-family: ${BODY};
    outline: none; transition: border-color .15s, box-shadow .15s; display: block;
  }
  .login-input:focus { border-color: ${C.sage}; box-shadow: 0 0 0 3px rgba(74,124,89,.12); }
  .btn-primary {
    width: 100%; padding: 14px; background: ${C.pine}; color: white;
    border: none; border-radius: 12px; cursor: pointer; font-size: 16px;
    font-weight: 700; font-family: ${BODY}; letter-spacing: -0.01em;
    transition: all .15s; display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .btn-primary:hover:not(:disabled) { background: ${C.forest}; transform: translateY(-1px); }
  .btn-primary:disabled { opacity: .55; cursor: not-allowed; }
  .btn-ghost-sm {
    background: none; border: none; cursor: pointer; font-family: ${BODY};
    font-size: 13px; font-weight: 500; transition: color .15s; padding: 0;
  }
  .verify-btn {
    padding: 11px 16px; background: white; border: 1.5px solid ${C.border};
    border-radius: 12px; cursor: pointer; font-size: 13px; font-weight: 700;
    font-family: ${BODY}; color: ${C.inkMid}; white-space: nowrap; transition: all .15s;
  }
  .verify-btn:hover { border-color: ${C.sage}; color: ${C.forest}; }
  .link-row {
    border-top: 1px solid ${C.border}; padding-top: 18px; text-align: center;
  }
`

const labelSt = {
  display: 'block', fontSize: 11, fontWeight: 700,
  color: C.inkLt, textTransform: 'uppercase',
  letterSpacing: '0.09em', marginBottom: 7,
}

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M1 9C1 9 4 3 9 3C14 3 17 9 17 9C17 9 14 15 9 15C4 15 1 9 1 9Z" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="9" cy="9" r="2.5" stroke="#94a3b8" strokeWidth="1.5" fill="none"/>
    {!open && <line x1="2" y1="2" x2="16" y2="16" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>}
  </svg>
)

function PasswordInput({ name, placeholder, value, onChange, onKeyPress, style }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input name={name} type={show ? 'text' : 'password'} placeholder={placeholder}
        value={value} onChange={onChange} onKeyPress={onKeyPress}
        className="login-input" style={{ ...style, paddingRight: '44px' }} />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
        tabIndex={-1}>
        <EyeIcon open={show} />
      </button>
    </div>
  )
}

function ErrorBox({ children }) {
  return (
    <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#dc2626', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx="8" cy="8" r="7" stroke="#dc2626" strokeWidth="1.5" fill="none"/>
        <line x1="8" y1="5" x2="8" y2="9" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="8" cy="11.5" r=".8" fill="#dc2626"/>
      </svg>
      {children}
    </div>
  )
}

export default function Login({ onLogin, onBackToHome, initialView = 'login', initialClinicSlug = '' }) {
  const [view, setView] = useState(initialView)
  const [form, setForm] = useState({ username: '', password: '', clinic_slug: initialClinicSlug })
  const [registerForm, setRegisterForm] = useState({
    clinic_name: '', clinic_slug: '', owner_email: '',
    admin_username: '', admin_password: '', admin_confirm: ''
  })
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [forgotUsername, setForgotUsername] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [clinicVerified, setClinicVerified] = useState(null)
  const [verifying, setVerifying] = useState(false)
  const [registeredData, setRegisteredData] = useState(null)
  const [blockedReason, setBlockedReason] = useState(null) // 'TRIAL_EXPIRED' | 'SUBSCRIPTION_CANCELLED'
  const [loginError, setLoginError] = useState('');

  // Auto-verify clinic when slug is pre-filled from URL param
  useEffect(() => {
    if (initialClinicSlug) {
      setVerifying(true)
      axios.get(API + '/clinics/verify/' + initialClinicSlug)
        .then(res => setClinicVerified(res.data))
        .catch(() => setError('Clinic code not found. Check with your clinic.'))
        .finally(() => setVerifying(false))
    }
  }, [initialClinicSlug])

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })
  const handleRegisterChange = (e) => setRegisterForm({ ...registerForm, [e.target.name]: e.target.value })

  const verifyClinic = () => {
    if (!form.clinic_slug) return
    setVerifying(true)
    axios.get(API + '/clinics/verify/' + form.clinic_slug)
      .then(res => { setClinicVerified(res.data); setError('') })
      .catch(() => { setClinicVerified(null); setError('Clinic code not found. Check with your clinic.') })
      .finally(() => setVerifying(false))
  }

  const handleSubmit = () => {
  if (!form.username || !form.password) {
    setError('Please enter your username and password.')
    return
  }

  const isSuperAdmin = form.username === 'superadmin'
  const isDemo = form.username === 'demo'

  if (!form.clinic_slug && !isDemo && !isSuperAdmin) {
    setError('Please enter your clinic code.')
    return
  }

  setLoading(true)
  setError('')

  axios.post(API + '/auth/login', {
    username: form.username,
    password: form.password,
    clinic_slug: isSuperAdmin
      ? null
      : isDemo
      ? 'DEMO'
      : form.clinic_slug
  })
    .then(res => {
      setLoading(false)
      onLogin(res.data.user, res.data.token)
    })
    .catch(err => {
      setLoading(false)
      const status = err.response?.status
      const detail = err.response?.data?.detail || ''
      if (status === 402) {
        setBlockedReason(detail) // 'TRIAL_EXPIRED' or 'SUBSCRIPTION_CANCELLED'
      } else {
        setError(detail || 'Incorrect username or password.')
      }
    })
}

  const handleRegister = () => {
    setError('')
    const { clinic_name, clinic_slug, owner_email, admin_username, admin_password, admin_confirm } = registerForm
    if (!clinic_name || !clinic_slug || !admin_username || !admin_password) { setError('Please fill in all required fields.'); return }
    if (admin_password !== admin_confirm) { setError('Passwords do not match.'); return }
    if (admin_password.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (!agreedToTerms) { setError('You must agree to the Business Associate Agreement and Terms of Service to continue.'); return }
    setLoading(true)
    axios.post(API + '/clinics/register', {
      clinic_name, clinic_slug: clinic_slug.toUpperCase(), owner_email, admin_username, admin_password,
      agreed_to_terms: true
    })
      .then(res => {
        setLoading(false)
        setForm({ ...form, clinic_slug: res.data.clinic_slug })
        setRegisteredData({ slug: res.data.clinic_slug, emailSent: res.data.email_sent, email: owner_email })
        setView('registered'); setError('')
      })
      .catch(err => { setLoading(false); setError(err.response?.data?.detail || 'Registration failed. Please try again.') })
  }

  const handleForgot = () => {
    if (!forgotUsername) { setError('Please enter your username.'); return }
    setLoading(true); setError('')
    axios.post(API + '/auth/forgot-password', { username: forgotUsername, clinic_slug: form.clinic_slug || null })
      .then(res => { setLoading(false); if (res.data.success) setView('sent'); else setError(res.data.message) })
      .catch(() => { setLoading(false); setError('Something went wrong. Please try again.') })
  }

  const viewTitles = {
    login:      ['Sign in to your account', null],
    forgot:     ['Reset your password', null],
    sent:       ['Check your email', null],
    register:   ['Start your free trial', '30 days free — no card required'],
    registered: ["You're all set", null],
  }
  const [title, subtitle] = viewTitles[view] || ['', null]

  //  Blocked screen (trial expired / subscription cancelled) 
  if (blockedReason) {
    const isTrialExpired = blockedReason === 'TRIAL_EXPIRED'
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: BODY, background: `linear-gradient(152deg, ${C.pine} 0%, #065f46 45%, #6d28d9 100%)` }}>
        <style>{CSS}</style>
        <div style={{ background: C.cream, borderRadius: 24, padding: '44px 40px', width: '100%', maxWidth: '480px', boxShadow: '0 40px 80px rgba(0,0,0,.3)', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{isTrialExpired ? '⏰' : ''}</div>
          <h2 style={{ fontFamily: DISPLAY, fontSize: 26, color: C.ink, fontWeight: 400, fontStyle: 'italic', margin: '0 0 12px' }}>
            {isTrialExpired ? 'Your free trial has ended' : 'Subscription cancelled'}
          </h2>
          <p style={{ fontSize: 15, color: C.inkLt, lineHeight: 1.65, margin: '0 0 8px' }}>
            {isTrialExpired
              ? 'Your 30-day trial for this clinic has expired. Your data is safe — choose a plan to pick up right where you left off.'
              : 'Your subscription has been cancelled and your account is currently inactive. Reactivate to restore access.'}
          </p>
          <p style={{ fontSize: 13, color: C.sage, fontWeight: 600, margin: '0 0 28px' }}>
            All patient data is preserved.
          </p>

          {/* Pricing link */}
          <a
            href="https://clearpath-pt.com/#pricing"
            style={{ display: 'block', padding: '14px 0', borderRadius: 12, background: `linear-gradient(135deg, #0d9488, #0f766e)`, color: 'white', textDecoration: 'none', fontSize: 16, fontWeight: 700, fontFamily: BODY, marginBottom: 12 }}
          >
            {isTrialExpired ? 'Choose a plan →' : 'Reactivate my account →'}
          </a>
          <button
            onClick={() => setBlockedReason(null)}
            style={{ width: '100%', padding: '12px 0', borderRadius: 12, border: `1.5px solid ${C.border}`, background: 'white', color: C.inkMid, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: BODY }}
          >
            ← Back to login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: BODY, position: 'relative', overflow: 'hidden', background: `linear-gradient(152deg, ${C.pine} 0%, #065f46 45%, #6d28d9 100%)` }}>
      <style>{CSS}</style>

      {/* Background blobs */}
      <div style={{ position: 'absolute', top: '-15%', right: '-8%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(110,231,183,0.15) 0%, transparent 70%)', borderRadius: '999px', animation: 'blob1 16s ease-in-out infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-18%', left: '-6%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)', borderRadius: '999px', animation: 'blob2 22s ease-in-out infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '64px 64px', pointerEvents: 'none' }} />

      <div className="login-card" style={{ background: C.cream, borderRadius: 24, padding: '40px 36px', width: '100%', maxWidth: '460px', boxShadow: '0 40px 80px rgba(0,0,0,.25)', position: 'relative', zIndex: 10 }}>

        {onBackToHome && (
          <button onClick={onBackToHome} className="btn-ghost-sm" style={{ color: C.inkLt, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke={C.inkLt} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to home
          </button>
        )}

        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: C.pine, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <path d="M16 4C16 4 9 9 9 17c0 3.9 3.1 7 7 7s7-3.1 7-7c0-8-7-13-7-13Z" stroke="white" strokeWidth="2" fill="none" strokeLinejoin="round"/>
                <line x1="16" y1="24" x2="16" y2="28" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <line x1="13" y1="27" x2="19" y2="27" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <line x1="13" y1="17" x2="19" y2="17" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
                <line x1="16" y1="14" x2="16" y2="20" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontFamily: DISPLAY, fontSize: 22, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1.1 }}>ClearPath PT</div>
              <div style={{ fontSize: 10, color: C.inkLt, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Practice Management</div>
            </div>
          </div>
          <h2 style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: 22, color: C.inkMid, fontWeight: 400, lineHeight: 1.2 }}>{title}</h2>
          {subtitle && <p style={{ fontSize: 13, color: C.inkLt, marginTop: 6 }}>{subtitle}</p>}
        </div>

        {/*  LOGIN  */}
        {view === 'login' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Clinic code — hidden for superadmin */}
            {form.username !== 'superadmin' && (
              <div>
                {/* If slug came from URL, show a locked verified badge instead of the input */}
                {initialClinicSlug ? (
                  <div style={{ background: C.foam, border: `1px solid ${C.mint}`, borderRadius: 14, padding: '14px 16px' }}>
                    {verifying ? (
                      <div style={{ fontSize: 13, color: C.inkLt }}>Verifying clinic…</div>
                    ) : clinicVerified ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: C.sage, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7 8,3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.forest }}>{clinicVerified.clinic_name}</span>
                        </div>
                        {clinicVerified.address && <div style={{ fontSize: 12, color: C.inkMid, marginLeft: 26 }}>{clinicVerified.address}</div>}
                        {clinicVerified.phone && <div style={{ fontSize: 12, color: C.inkMid, marginLeft: 26 }}>{clinicVerified.phone}</div>}
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: '#dc2626' }}>Clinic not found — check your link.</div>
                    )}
                  </div>
                ) : (
                  <>
                    <label style={labelSt}>Clinic Code</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input name="clinic_slug" placeholder="e.g. CLEARPATH" value={form.clinic_slug}
                        onChange={e => { handleChange(e); setClinicVerified(null) }}
                        onKeyPress={e => e.key === 'Enter' && verifyClinic()}
                        className="login-input" style={{ flex: 1, textTransform: 'uppercase' }} />
                      <button onClick={verifyClinic} disabled={verifying} className="verify-btn">
                        {verifying ? '…' : 'Verify'}
                      </button>
                    </div>
                    {clinicVerified && (
                      <div style={{ marginTop: 10, background: C.foam, border: `1px solid ${C.mint}`, borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: C.sage, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="2,5 4,7 8,3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.forest }}>{clinicVerified.clinic_name}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {clinicVerified.address && <div style={{ fontSize: 12, color: C.inkMid }}>{clinicVerified.address}</div>}
                          {clinicVerified.phone && <div style={{ fontSize: 12, color: C.inkMid }}>{clinicVerified.phone}</div>}
                          {clinicVerified.email && <div style={{ fontSize: 12, color: C.inkMid }}>{clinicVerified.email}</div>}
                          {!clinicVerified.address && !clinicVerified.phone && !clinicVerified.email && (
                            <div style={{ fontSize: 12, color: C.inkLt }}>Clinic verified — enter your credentials below</div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div>
              <label style={labelSt}>Username</label>
              <input name="username" placeholder="Enter your username" value={form.username}
                onChange={handleChange} onKeyPress={e => e.key === 'Enter' && handleSubmit()}
                className="login-input" />
            </div>

            <div>
              <label style={labelSt}>Password</label>
              <PasswordInput name="password" placeholder="Enter your password" value={form.password}
                onChange={handleChange} onKeyPress={e => e.key === 'Enter' && handleSubmit()} />
            </div>

            <div style={{ textAlign: 'right', marginTop: -6 }}>
              <button onClick={() => { setView('forgot'); setError('') }} className="btn-ghost-sm" style={{ color: C.sage }}>Forgot password?</button>
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button onClick={handleSubmit} disabled={loading} className="btn-primary">
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            <div className="link-row">
              <button onClick={() => { setView('register'); setError('') }} className="btn-ghost-sm" style={{ color: C.inkLt }}>
                No account? <span style={{ color: C.sage, fontWeight: 600 }}>Start free trial →</span>
              </button>
            </div>
          </div>
        )}

        {/*  REGISTER  */}
        {view === 'register' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: C.foam, border: `1px solid ${C.mint}`, borderRadius: 12, padding: '12px 16px', fontSize: 13, color: C.forest, lineHeight: 1.6 }}>
              Your clinic code lets your staff and patients log in — letters only, no spaces.
            </div>

            <div>
              <label style={labelSt}>Clinic Name *</label>
              <input name="clinic_name" placeholder="e.g. River Valley Physical Therapy" value={registerForm.clinic_name} onChange={handleRegisterChange} className="login-input" />
            </div>
            <div>
              <label style={labelSt}>Clinic Code *</label>
              <input name="clinic_slug" placeholder="e.g. RIVERVALLEY" value={registerForm.clinic_slug}
                onChange={e => setRegisterForm({ ...registerForm, clinic_slug: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
                className="login-input" style={{ textTransform: 'uppercase' }} />
            </div>
            <div>
              <label style={labelSt}>Owner Email</label>
              <input name="owner_email" type="email" placeholder="your@email.com" value={registerForm.owner_email} onChange={handleRegisterChange} className="login-input" />
            </div>

            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: C.inkMid, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 14 }}>Your Admin Account</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={labelSt}>Username *</label>
                  <input name="admin_username" placeholder="e.g. drsmith" value={registerForm.admin_username} onChange={handleRegisterChange} className="login-input" />
                </div>
                <div>
                  <label style={labelSt}>Password *</label>
                  <PasswordInput name="admin_password" placeholder="At least 6 characters" value={registerForm.admin_password} onChange={handleRegisterChange} />
                </div>
                <div>
                  <label style={labelSt}>Confirm Password *</label>
                  <PasswordInput name="admin_confirm" placeholder="Re-enter password" value={registerForm.admin_confirm} onChange={handleRegisterChange} />
                </div>
              </div>
            </div>

            {/* Legal agreement checkbox */}
            <div
              onClick={() => setAgreedToTerms(v => !v)}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: agreedToTerms ? C.foam : '#f8fafc', border: `1.5px solid ${agreedToTerms ? C.mint : C.border}`, borderRadius: 10, cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s' }}
            >
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${agreedToTerms ? C.pine : '#cbd5e1'}`, background: agreedToTerms ? C.pine : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'all 0.15s' }}>
                {agreedToTerms && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </div>
              <span style={{ fontSize: 13, color: C.inkMid, lineHeight: 1.55 }}>
                I have read and agree to the{' '}
                <a href="/baa" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.pine, fontWeight: 600, textDecoration: 'underline' }}>Business Associate Agreement</a>
                {' '}and{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.pine, fontWeight: 600, textDecoration: 'underline' }}>Terms of Service</a>.
                {' '}This is required by HIPAA before accessing patient data.
              </span>
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button onClick={handleRegister} disabled={loading} className="btn-primary">
              {loading ? 'Creating your clinic…' : 'Start Free Trial'}
            </button>
            <div className="link-row">
              <button onClick={() => { setView('login'); setError('') }} className="btn-ghost-sm" style={{ color: C.inkLt }}>
                ← Already have an account? <span style={{ color: C.sage, fontWeight: 600 }}>Sign in</span>
              </button>
            </div>
          </div>
        )}

        {/*  REGISTERED  */}
        {view === 'registered' && registeredData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: C.pine, borderRadius: 18, padding: '24px', textAlign: 'center' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Your Clinic Code</p>
              <div style={{ fontFamily: DISPLAY, fontSize: 36, fontWeight: 700, color: 'white', letterSpacing: '0.12em', marginBottom: 10 }}>{registeredData.slug}</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>Save this — you and your staff enter it every time you log in.</p>
            </div>

            {registeredData.emailSent ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '13px 16px', fontSize: 13, color: '#15803d', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="8" cy="8" r="8" fill="#16a34a"/><polyline points="4.5,8 7,10.5 11.5,5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
                <span>Welcome email sent to <strong>{registeredData.email}</strong>.</span>
              </div>
            ) : (
              <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 12, padding: '13px 16px', fontSize: 13, color: '#92400e', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="8" cy="8" r="8" fill="#d97706"/><line x1="8" y1="4.5" x2="8" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/><circle cx="8" cy="11.5" r=".8" fill="white"/></svg>
                <span>Welcome email could not be sent — your clinic code is shown above. You can sign in now.</span>
              </div>
            )}

            <button onClick={() => { setView('login'); setError('') }} className="btn-primary" style={{ marginTop: 4 }}>
              Sign In Now
            </button>
          </div>
        )}

        {/*  FORGOT  */}
        {view === 'forgot' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 14, color: C.inkLt, lineHeight: 1.7 }}>Enter your username and we'll send a reset link to the email on file.</p>
            <div>
              <label style={labelSt}>Username</label>
              <input placeholder="Enter your username" value={forgotUsername}
                onChange={e => setForgotUsername(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleForgot()}
                className="login-input" />
            </div>
            {error && <ErrorBox>{error}</ErrorBox>}
            <button onClick={handleForgot} disabled={loading} className="btn-primary">
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
            <div className="link-row">
              <button onClick={() => { setView('login'); setError('') }} className="btn-ghost-sm" style={{ color: C.inkLt }}>← Back to sign in</button>
            </div>
          </div>
        )}

        {/*  SENT  */}
        {view === 'sent' && (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
            <div style={{ width: 60, height: 60, background: C.foam, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${C.mint}` }}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M4 8L14 16L24 8" stroke={C.sage} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="2" y="6" width="24" height="16" rx="3" stroke={C.sage} strokeWidth="2" fill="none"/>
              </svg>
            </div>
            <p style={{ fontSize: 15, color: C.inkMid, lineHeight: 1.7 }}>
              If that username is in our system, a reset link has been sent. Check your inbox — it expires in 1 hour.
            </p>
            <button onClick={() => { setView('login'); setError(''); setForgotUsername('') }} className="btn-primary">
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}